import DesignTokens from '@tableau/design-tokens';
import { NotificationAlertErrorShieldSolidIcon } from '@tableau/icons/NotificationAlertErrorShieldSolidIcon';
import { TabStyles,TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog, ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FlowParametersGrid } from '~src/flow/components/FlowParametersGrid';
import { FlowSubscriptionCount } from '~src/flow/components/FlowSubscriptionCount';
import { buttonSize, FlowTaskInfoItem, itemClass } from '~src/flow/components/FlowTaskInfoItem';
import { headerStyle, scheduleIcon, scheduleIconWrapper } from '~src/flow/components/FlowTaskOutputsDialog';
import { FlowTaskOutputsGrid } from '~src/flow/components/FlowTaskOutputsGrid';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';
import { areFlowParametersEnabled } from '~src/flow/utils/FlowParametersUtils';
import { isFlowSelfServiceSchedulesEnabled } from '~src/flow/utils/FlowSelfServiceSchedulesUtils';
import { LinkedTaskInfo } from '~src/linkedTasks/models/LinkedTaskInfo';
import { LinkedTaskItemInfo } from '~src/linkedTasks/models/LinkedTaskItemInfo';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { isServerTasksSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { classes, cssClass } from '~src/style';
import { applyColor }from '~src/style/ColorService';
import { errorActionRed } from '~src/userNotifications/styles/notificationStyles';

import { LinkedTasks } from '../resources/LinkedTasks';
import { editLinkedTaskDialog } from './EditLinkedTaskDialog';
import { viewingNumberClass } from './LinkedTaskItem';


const rootClass = 'LinkedTaskDetailsDialog';

const scrollWrapper = cssClass(rootClass, 'scrollWrapper', {
  height: 'calc(100% - 36px)',
  overflow: 'auto',
});

const taskListWrapper = cssClass(rootClass, 'taskListWrapper', {
  marginTop: 4,
  $nest: {
    '& > div:not(:first-child)': {
      marginTop: 16,
    },
  },
});

const taskWrapperStyle = cssClass(rootClass, 'wrapperStyle', {
  display: 'flex',
  alignItems: 'baseline',
});

const flowErrorStyle = cssClass(rootClass, 'flowErrorStyle', {
  width: '100%',
  height: buttonSize,
  color: applyColor(DesignTokens.color.font.default.error, errorActionRed),
});

const errorIconStyle = cssClass(rootClass, 'errorIconStyle', {
  marginRight: TabStyles.Spacing.DefaultSpace,
});

const gridHeader = cssClass(rootClass, 'gridHeader', {
  fontWeight: 500,
  paddingBottom: 8,
});

const expansionWrapper = cssClass(rootClass, 'expansionWrapper', {
  border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L6)}`,
  borderTop: 0,
  padding: TabStyles.Spacing.MediumSpace,
  $nest: {
    '& .tb-react-dg-hrow': { borderTop: 0 }
  },
});

const sectionWrapper = cssClass(rootClass, 'sectionWrapper', {
  paddingBottom: TabStyles.Spacing.MediumSpace,
});

const sectionBorder = cssClass(rootClass, 'sectionBorder', {
  border: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L6)}`,
  boxSizing: 'border-box',
  height: 36,
});

const countWrapper = cssClass(rootClass, 'countWrapper', {
  height: 36,
  lineHeight: '36px',
  paddingLeft: TabStyles.Spacing.MediumSpace,
});

const topPadding = cssClass(rootClass, 'topPadding', {
  paddingTop: 8,
});

function onAction(props: LinkedTaskDetailsDialogProps) {
  editLinkedTaskDialog(props.linkedTask);
  return Promise.resolve();
}

export function getAndOpenLinkedTaskDetailsDialog(linkedTaskId: string) {
    const appState = getAppStore().getState();
    const spaceInfo: SpaceInfo = selectSpaceInfo(appState);

  if (isServerTasksSpace(spaceInfo)) {
    return LinkedTasks.getLinkedTaskForServer(linkedTaskId).then(response => {
      return openLinkedTaskDetailsDialog({ linkedTask: response });
    });
  } else {
    return LinkedTasks.getLinkedTask(linkedTaskId).then(response => {
      return openLinkedTaskDetailsDialog({ linkedTask: response });
    });
  }
}

export function openLinkedTaskDetailsDialog(props: LinkedTaskDetailsDialogProps) {
  const modalInstance = openReactActionDialog({
    title: Messages.linkedTasks(),
    bodyComponent: LinkedTaskDetailsDialogContent,
    initialBodyProps: {
      ...props,
    },
    initialDialogState: {},
    actionName: Messages.linkedTasksEditTasks(),
    action: onAction,
    dialogWidth: 800,
    dialogHeight: 450,
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
    cancelButtonText: Messages.close(),
  });

  return modalInstance.result;
}

export interface LinkedTaskDetailsDialogProps {
  linkedTask: LinkedTaskInfo;
}

export type LinkedTaskDetailsActionDialogProps = ReactActionDialogBodyProps<LinkedTaskDetailsDialogProps, {}>;

export function LinkedTaskDetailsDialogContent(props: LinkedTaskDetailsActionDialogProps): JSX.Element {
  if (!props.linkedTask) return null;

  function renderScheduleText(): JSX.Element {
    const siteUrls = siteUrlsSelector(getAppStore().getState());
    const isUserSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    const scheduleDisplay = isFlowSelfServiceSchedulesEnabled() ?
      IScheduleInfo.getRecurrenceDescription(props.linkedTask.scheduledTaskFrequency) :
      props.linkedTask.schedule?.name;
    return isUserSiteOrServerAdmin && !isFlowSelfServiceSchedulesEnabled()?
      <TextLinkWidget
        href={siteUrls.scheduleFlowsUrl(props.linkedTask.schedule?.id)}
        title={props.linkedTask.schedule?.name}
        testId={`linked-task-detail-dialog-link-${props.linkedTask.schedule?.id}`}
      >
        {scheduleDisplay}
      </TextLinkWidget>
      :
      <div title={scheduleDisplay}>
        {scheduleDisplay}
      </div>;
  }

  function renderEmptyTask(stepNumber: number, isTransient: boolean): JSX.Element {
    const errorMessage = isTransient
      ? Messages.linkedTasksFlowNotFoundTransient()
      : Messages.linkedTasksFlowNotFoundError();
    return (
      <div className={taskWrapperStyle}>
        <div className={viewingNumberClass}>{stepNumber}</div>
        <div className={classes(itemClass, flowErrorStyle)}>
          <NotificationAlertErrorShieldSolidIcon size={14} alt={''} className={errorIconStyle} />
          {errorMessage}
        </div>
      </div>
    );
  }

  function renderOutputsGrid(outputIds: readonly string[], outputSteps: readonly FlowOutputStepInfo[]): JSX.Element {
    return (
      <div className={sectionWrapper}>
        <div className={gridHeader}>{Messages.flowOutputsHeader()}</div>
        <FlowTaskOutputsGrid outputIds={outputIds} outputSteps={outputSteps} />
      </div>
    );
  }

  function renderSubscriptionCount(count: number, flowId: string) {
    return (
      <>
        <div className={gridHeader}>{Messages.subscriptions()}</div>
        <div className={sectionBorder}>
          <div className={countWrapper}>
            <FlowSubscriptionCount
              count={count}
              showCheckmark={false}
              flowId={flowId}
            />
          </div>
        </div>
      </>
    );
  }

  function renderParametersGrid(linkedTaskItem: LinkedTaskItemInfo): JSX.Element {
    return linkedTaskItem.flowParameterParams.length > 0 && (
      <div className={topPadding}>
        <div className={gridHeader}>{Messages.flowParametersHeader()}</div>
        <FlowParametersGrid parameters={linkedTaskItem.flowParameters} parameterParams={linkedTaskItem.flowParameterParams}/>
      </div>
    );
  }

  function renderExpansionContent(linkedTaskItem: LinkedTaskItemInfo): JSX.Element {
    const flowOutputSubscriptionsEnabledOnSite = SessionSiteSelectors.isFlowOutputSubscriptionsEnabled(getAppStore().getState());

    return (
      <div className={expansionWrapper}>
        {renderOutputsGrid(linkedTaskItem.flowOutputStepIds, linkedTaskItem.flowOutputSteps)}
        {areFlowParametersEnabled() && renderParametersGrid(linkedTaskItem)}
        {flowOutputSubscriptionsEnabledOnSite && linkedTaskItem?.subscriptionFlow?.subscriptionFlowMetadata?.subscriberCount &&
          renderSubscriptionCount(linkedTaskItem.subscriptionFlow.subscriptionFlowMetadata.subscriberCount, linkedTaskItem.targetId)}
      </div>
    );
  }

  function renderTasks(): JSX.Element[] {

    return props.linkedTask.linkedTaskItems.map((linkedTaskItem: LinkedTaskItemInfo, index: number) => {
      if (!linkedTaskItem?.id) {
        return renderEmptyTask(linkedTaskItem?.stepNumber ?? index + 1, !!linkedTaskItem?.isTransientFlowNotFound);
      }
      return (
        <div key={`${linkedTaskItem.id}-${index}`} className={taskWrapperStyle}>
          <div className={viewingNumberClass}>{linkedTaskItem.stepNumber}</div>
          <FlowTaskInfoItem
            taskId={linkedTaskItem.id}
            flowName={linkedTaskItem.name}
            flowId={linkedTaskItem.targetId}
            lastRun={linkedTaskItem.lastRunAt ? new Date(linkedTaskItem.lastRunAt) : null}
            taskStatus={linkedTaskItem.status}
            shouldObfuscate={linkedTaskItem.shouldObfuscate}
            expansionContent={linkedTaskItem.shouldObfuscate ? null :
              renderExpansionContent(linkedTaskItem)
            }
          />
        </div>
      );
    });
  }

  return (
    <div className={scrollWrapper}>
      <div className={headerStyle}>
        <div className={scheduleIconWrapper}>
          <img src={imgPath('schedule_content_type_small.svg')} className={scheduleIcon} alt={''} />
          {Messages.schedule()}
        </div>
        <div>{renderScheduleText()}</div>
      </div>
      <div className={taskListWrapper}>
        {renderTasks()}
      </div>
    </div>
  );
}
